import { Box, IconButton, Typography, Button } from "@mui/material";
import { format, parseISO } from "date-fns";
import { forwardRef, useCallback, useMemo } from "react";
import { useRequestSyncVisitMutation } from "src/api/services/encounterApi";
import { TimeSlot, Specialties } from "src/api/types";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";

interface Props4 {
  goBack: () => void;
  pickedTime: TimeSlot | null;
  pickedDate: Date | null;
  onClose: () => void;
}
const ConfirmSlide = forwardRef<HTMLDivElement, Props4>(
  ({ goBack, pickedTime, pickedDate, onClose, ...rest }, ref) => {
    const specialty: Specialties = "General";
    const [requestSyncVisit, { isLoading, error }] =
      useRequestSyncVisitMutation();

    const handleRequestSyncVisit = useCallback(async () => {
      if (pickedTime == null) return;
      requestSyncVisit({
        specialty,
        ...pickedTime,
      }).then(onClose);
    }, [pickedTime, requestSyncVisit, onClose]);

    const formattedTime =
      pickedTime != null ? format(parseISO(pickedTime.start_at), "H:mm") : "";

    const errorMessage = useMemo(() => {
      if (error != null) {
        return error instanceof Error
          ? error.message
          : "Something went wrong please try again later";
      }
    }, [error]);

    return (
      <Box
        ref={ref}
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
        }}
        {...rest}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1.5,
            pb: 1,
          }}
        >
          <IconButton size="small" onClick={goBack} aria-label="Change time">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
            Confirm your slot
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            px: 3,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              mb: 1,
            }}
          >
            <CheckIcon />
          </Box>
          <Typography variant="h5" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {formattedTime}
          </Typography>
          <Typography color="text.secondary">
            {pickedDate && format(pickedDate, "EEEE, MMMM d, yyyy")}
          </Typography>
        </Box>
        {errorMessage != null ? (
          <Typography color="red">{errorMessage}</Typography>
        ) : null}
        <Box sx={{ display: "flex", gap: 1, p: 1.5 }}>
          <Button fullWidth variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={isLoading}
            fullWidth
            variant="contained"
            disableElevation
            onClick={handleRequestSyncVisit}
          >
            Confirm
          </Button>
        </Box>
      </Box>
    );
  },
);

export default ConfirmSlide;
